import React, { useEffect, useState, useRef } from "react";
import Decimal from "decimal.js";
import {
  add_funds_to_from_borrower,
  current_loan_balance_first_td,
  loan_amount_first_mortgage_base,
  loan_amount_second_mortgage,
  solve_heloc_second_for_target,
  solve_new_first_loan_for_target,
  total_closing_costs,
  va_add_to_loan
} from "../../../calculations/loan";
import CurrencyField from "../../fields/currency_field";
import PercentField from "../../fields/percent_field";
import SelectField from "../../fields/select_field";
import ComboBox from "../../fields/combo_box";
import { getAllLenders } from "../../../constants/custom_lenders";
import { WHOLESALE_LENDERS } from "../../../constants/lender_registry";
import { YES, YES_NO } from "../../../constants/yes_no";
import { PERCENT_PAYMENT_TYPES } from "../../../constants/payment_type";
import { useDocument } from "../loan_tool";
import {
  balance_pay_offs_total,
  payoff_balance_at_position,
  payoff_other_debt_total
} from "../../../calculations/debt_info";
import { SECOND_POSITION, THIRD_POSITION } from "../../../constants/lien_positions";
import { default_loan } from "../../../schema/loan";
import Section from "../../ui/section";
import { is_va, will_second_subordinate } from "../../../constants/utils";
import { HELOC, FIXED_RATE_SECOND } from "../../../constants/loan_types";
import { SECOND_LOAN_AMOUNT } from "../../../constants/second_loan_options";

const CLTV_OVER_LIMIT_TIP =
  "It appears you are over the Maximum guideline for CLTV. Check with your " +
  "lender for current guidelines. You may be out of Compliance and the loan " +
  "will not be approved.";

export default function RefinanceLoanCalculator({ loan, goBack, updateLoan }) {
  const [
    {
      property,
      property_to_be_sold,
      is_selling_property,
      accountSummaries,
      residences
    },
    ,
    { documentType },
    { saveDocument }
  ] = useDocument();
  const [loanCalculator, setLoanCalculator] = useState(() => {
    const merged = {
      ...default_loan.refinance_loan_calculator,
      ...loan.refinance_loan_calculator
    };
    // One cash-out number: seed the Target from the Main page "Cash Out (Add to
    // Loan)" so an existing file carries over, and the two stay in sync.
    const mainCashOut = loan.refinance_cash_out_add_to_loan_amount;
    if (
      (!merged.target_cash_to_borrower ||
        merged.target_cash_to_borrower === "0") &&
      mainCashOut &&
      mainCashOut !== "0"
    ) {
      merged.target_cash_to_borrower = mainCashOut;
    }
    return merged;
  });
  // The Target is shown in red until the LO clicks Calculate, so they can't
  // forget to re-solve the loan amount for the new cash-out figure.
  const [targetUncalculated, setTargetUncalculated] = useState(false);
  const callback = useRef();

  useEffect(() => {
    callback.current = () => {
      if (loanCalculator.is_modified) {
        if (
          window.confirm(
            "You have unsaved data in Refinance Calculator. Would you like to save it before you leave?"
          )
        ) {
          save();
        }
      }
    };
  }, [loanCalculator]);

  useEffect(() => {
    return () => {
      callback.current();
    };
  }, []);

  function save() {
    delete loanCalculator.is_modified;
    const updates = { refinance_loan_calculator: loanCalculator };
    // Mirror the single cash-out number back to the Main page "Cash Out (Add to
    // Loan) $" so both pages show it. (A 2nd-lien's cash comes from the 2nd
    // amount, not this field.)
    if (loan.type !== HELOC && loan.type !== FIXED_RATE_SECOND) {
      updates.refinance_cash_out_add_to_loan_amount =
        loanCalculator.target_cash_to_borrower || "0";
      updates.refinance_cash_out_add_to_loan_amount_percent = "0";
    }
    updateLoan(updates);
    saveDocument();
    goBack();
  }

  function cancel() {
    goBack();
  }

  function updateLoanCalculator(updatedState) {
    setLoanCalculator({
      ...loanCalculator,
      ...updatedState,
      is_modified: true
    });
  }

  // HELOC and Fixed Rate Second are both 2nd mortgages and configure the same
  // way (size the new 2nd, keep the 1st in place). Both use the 2nd-mortgage
  // layout + the Calculate 2nd Loan Amount solver.
  const isSecondMortgage =
    loan.type === HELOC || loan.type === FIXED_RATE_SECOND;

  // Use the in-progress calculator edits (not the last-saved loan) so the loan-
  // amount-dependent fees and Add Funds update live as the LO types, matching
  // the Summary Sheet.
  const loanForCalc = { ...loan, refinance_loan_calculator: loanCalculator };

  const addFunds = add_funds_to_from_borrower(
    documentType,
    loanCalculator,
    loanForCalc,
    accountSummaries,
    residences,
    property,
    property_to_be_sold,
    is_selling_property
  );

  // Solve the New 1st Loan amount to hit the LO's target cash back, including
  // the fees that scale with the loan amount (origination, broker, prepaid int).
  function calculateLoanAmount() {
    const solved = solve_new_first_loan_for_target(
      loanCalculator.target_cash_to_borrower,
      documentType,
      loanForCalc,
      accountSummaries,
      residences,
      property,
      property_to_be_sold,
      is_selling_property
    );
    updateLoanCalculator({ total_of_new_first_loan: solved.toString() });
    setTargetUncalculated(false);
  }

  // HELOC version: solve the New 2nd (HELOC) loan amount for the target cash back.
  function calculateHelocAmount() {
    const solved = solve_heloc_second_for_target(
      loanCalculator.target_cash_to_borrower,
      documentType,
      loanForCalc,
      accountSummaries,
      residences,
      property,
      property_to_be_sold,
      is_selling_property
    );
    updateLoan({
      second_mortgage_loan_amount: solved.toString(),
      second_mortgage_loan_percent_or_amount: SECOND_LOAN_AMOUNT
    });
    setTargetUncalculated(false);
  }

  // ── 2nd-mortgage layout (HELOC or Fixed Rate Second) ──────────────────────
  if (isSecondMortgage) {
    const marketValue = new Decimal(property.listed_price || 0);
    const maxCltv = new Decimal(loanCalculator.max_cltv_heloc || 0);
    const maxCombined = marketValue.times(maxCltv).div(100);
    const current1st = current_loan_balance_first_td(
      property_to_be_sold,
      is_selling_property
    );
    const maxHeloc2nd = maxCombined.sub(current1st);
    const newSecond = loan_amount_second_mortgage(
      documentType,
      loan,
      property,
      property_to_be_sold,
      is_selling_property
    );
    const resultingCltv = marketValue.gt(0)
      ? current1st.add(newSecond).div(marketValue).times(100)
      : new Decimal(0);
    const overCeiling = maxCltv.gt(0) && newSecond.gt(maxHeloc2nd);

    return (
      <Section className="refinance-loan-calculator" name="Loan Calculator">
        {/* 1 · Max HELOC sizing (CLTV) */}
        <CurrencyField readOnly name="Market Value" value={marketValue} />
        <PercentField
          name="Maximum CLTV with 2nd Loan"
          fieldName="max_cltv_heloc"
          value={loanCalculator.max_cltv_heloc}
          placeholder="89.99"
          decimalPlaces={2}
          onChange={updateLoanCalculator}
        />
        <CurrencyField
          readOnly
          name="Max Combined Loan Amount"
          value={maxCombined}
        />
        <CurrencyField
          readOnly
          name="Current 1st TD Balance"
          value={current1st}
        />
        <CurrencyField
          readOnly
          name="Max 2nd (ceiling)"
          value={maxHeloc2nd}
        />
        <PercentField
          readOnly
          name="Resulting CLTV"
          decimalPlaces={3}
          value={resultingCltv.toFixed(3)}
        />

        <p className="bold">Cash to / from borrower</p>

        {/* 2 · Cash to / from borrower */}
        <CurrencyField
          name="Total of New 2nd Loan"
          fieldName="second_mortgage_loan_amount"
          value={loan.second_mortgage_loan_amount}
          className={overCeiling ? "heloc-over-limit" : ""}
          title={CLTV_OVER_LIMIT_TIP}
          onChange={updates =>
            updateLoan({
              second_mortgage_loan_amount: updates.second_mortgage_loan_amount,
              second_mortgage_loan_percent_or_amount: SECOND_LOAN_AMOUNT
            })
          }
        />
        <SelectField
          name="Second Mortgage Brokered?"
          fieldName="second_mortgage_brokered"
          value={loan.second_mortgage_brokered}
          onChange={updateLoan}
          selectOptions={YES_NO}
        />
        {loan.second_mortgage_brokered === YES && (
          <ComboBox
            id="second_mortgage_brokered_to_lender"
            name="2nd Brokered To (internal)"
            fieldName="second_mortgage_brokered_to_lender"
            value={loan.second_mortgage_brokered_to_lender}
            onChange={updateLoan}
            options={getAllLenders(WHOLESALE_LENDERS)}
            placeholder="Wholesale / 2nd-lien lender brokered to…"
            title="Internal only. The wholesale lender the second mortgage is brokered to. Used for management reporting - never shown to the borrower."
          />
        )}
        <CurrencyField
          name="Estimated Cash to Borrower (Target)"
          fieldName="target_cash_to_borrower"
          value={loanCalculator.target_cash_to_borrower}
          className={targetUncalculated ? "cash-out-uncalculated" : ""}
          onChange={updates => {
            updateLoanCalculator(updates);
            setTargetUncalculated(true);
          }}
          title="LANIS Training Tip: Enter the cash back you want the borrower to receive (a ~$1,500 pad keeps them positive), then click Calculate 2nd Loan Amount. LANIS solves the New 2nd loan amount for you, including the fees that depend on it. Fill in the Closing Costs first for an accurate result. Watch the CLTV ceiling above. The LO can also just type a higher 2nd amount if the borrower wants more cash out."
        />
        <button className="button" onClick={calculateHelocAmount}>
          Calculate 2nd Loan Amount
        </button>
        <p className="cash-out-calc-note">
          You must click Calculate 2nd Loan Amount to set the final loan amount.
        </p>
        <CurrencyField
          readOnly
          name="Current Loan Balance 2nd TD"
          value={payoff_balance_at_position(
            Object.values(accountSummaries),
            residences,
            loan,
            SECOND_POSITION
          )}
        />
        <CurrencyField
          readOnly
          name="Current Loan Balance 3rd TD"
          value={payoff_balance_at_position(
            Object.values(accountSummaries),
            residences,
            loan,
            THIRD_POSITION
          )}
        />
        <CurrencyField
          readOnly
          name="Pay Off Debt"
          value={payoff_other_debt_total(
            Object.values(accountSummaries),
            residences,
            loan
          )}
        />
        <CurrencyField
          readOnly
          name="Total of All Debt to be Paid Off"
          value={balance_pay_offs_total(
            Object.values(accountSummaries),
            residences,
            loan
          )}
        />
        <CurrencyField
          name="Prepay Penalty"
          fieldName="prepay_penalty"
          value={loanCalculator.prepay_penalty}
          onChange={updateLoanCalculator}
        />
        <CurrencyField
          readOnly
          name="Total Closing Costs"
          value={total_closing_costs(
            documentType,
            loan,
            property,
            property_to_be_sold,
            is_selling_property
          )}
        />
        <CurrencyField
          name="Closing Cost Pad"
          fieldName="closing_cost_pad"
          value={loanCalculator.closing_cost_pad}
          onChange={updateLoanCalculator}
        />
        <CurrencyField
          readOnly
          name="Add Funds To / From Borrower"
          value={addFunds}
        />

        <button className="button" onClick={cancel}>
          Cancel
        </button>
        <button className="button" onClick={save}>
          Save
        </button>
      </Section>
    );
  }

  // ── Standard (non-HELOC) layout — unchanged ───────────────────────────────
  return (
    <Section className="refinance-loan-calculator" name="Loan Calculator">
      <CurrencyField
        readOnly
        name="Max Loan Amount"
        value={loan_amount_first_mortgage_base(documentType, loan, property)}
      />

      {PERCENT_PAYMENT_TYPES.includes(loan.down_payment_type) && (
        <PercentField readOnly value={loan.percent_down} />
      )}

      <p className="bold">
        *May include FHA - VA - USDA Up front Fee if Financed
      </p>

      <CurrencyField
        name="Estimated Cash to Borrower (Target)"
        fieldName="target_cash_to_borrower"
        value={loanCalculator.target_cash_to_borrower}
        className={targetUncalculated ? "cash-out-uncalculated" : ""}
        onChange={updates => {
          updateLoanCalculator(updates);
          setTargetUncalculated(true);
        }}
        title="LANIS Training Tip: Enter the cash back you want the borrower to receive (a ~$1,500 pad keeps them positive), then click Calculate Loan Amount. LANIS solves the New 1st Loan for you, including the fees that depend on the loan amount (Loan Origination, Broker, and Prepaid Interest). Fill in the Closing Costs first for an accurate result."
      />
      <button className="button" onClick={calculateLoanAmount}>
        Calculate Loan Amount
      </button>
      <p className="cash-out-calc-note">
        You must click Calculate Loan Amount to set the final loan amount.
      </p>
      <CurrencyField
        name="Total of New 1st Loan"
        fieldName="total_of_new_first_loan"
        value={loanCalculator.total_of_new_first_loan}
        onChange={updateLoanCalculator}
      />
      <CurrencyField
        readOnly
        name="Total of New 2nd Loan"
        value={loan_amount_second_mortgage(
          documentType,
          loan,
          property,
          property_to_be_sold,
          is_selling_property
        )}
      />
      <CurrencyField
        readOnly
        name="Current Loan Balance 1st TD"
        value={current_loan_balance_first_td(
          property_to_be_sold,
          is_selling_property
        )}
      />
      <CurrencyField
        readOnly
        name="Current Loan Balance 2nd TD"
        value={payoff_balance_at_position(
          Object.values(accountSummaries),
          residences,
          loan,
          SECOND_POSITION
        )}
      />
      <CurrencyField
        readOnly
        name="Current Loan Balance 3rd TD"
        value={payoff_balance_at_position(
          Object.values(accountSummaries),
          residences,
          loan,
          THIRD_POSITION
        )}
      />
      <CurrencyField
        readOnly
        name="Pay Off Debt"
        value={payoff_other_debt_total(
          Object.values(accountSummaries),
          residences,
          loan
        )}
      />
      <CurrencyField
        readOnly
        name="Total of All Debt to be Paid Off"
        value={balance_pay_offs_total(
          Object.values(accountSummaries),
          residences,
          loan
        )}
      />
      <CurrencyField
        name="Prepay Penalty"
        fieldName="prepay_penalty"
        value={loanCalculator.prepay_penalty}
        onChange={updateLoanCalculator}
      />
      {is_va(loan.type) && (
        <CurrencyField
          readOnly
          name="VA Add UFF To Loan"
          value={va_add_to_loan(loan)}
        />
      )}
      <CurrencyField
        readOnly
        name="Total Closing Costs"
        value={total_closing_costs(
          documentType,
          loanForCalc,
          property,
          property_to_be_sold,
          is_selling_property
        )}
      />
      <CurrencyField
        name="Closing Cost Pad"
        fieldName="closing_cost_pad"
        value={loanCalculator.closing_cost_pad}
        onChange={updateLoanCalculator}
      />
      <CurrencyField
        readOnly
        name="Add Funds To / From Borrower"
        value={addFunds}
      />

      <button className="button" onClick={cancel}>
        Cancel
      </button>
      <button className="button" onClick={save}>
        Save
      </button>
    </Section>
  );
}
